import axios from "axios";
import navData from "./navigation_paths.json";

// Function to get navigation suggestions based on natural language input
export async function getNavigationSuggestions(userInput, availableRoutes) {
  try {
    // Prepare allowed routes from JSON (single source of truth)
    const routes = Array.isArray(navData?.routes) ? navData.routes : [];
    const allowedPaths = new Set(routes.map(r => r.path));

    // Backend UI-Agent configuration
    const baseUrl = process.env.REACT_APP_UI_AGENT_URL || "https://docinsight-sriven-api.azurewebsites.net/ww_ui_agent/ww_ui_agent";
    const authToken = process.env.REACT_APP_UI_AGENT_TOKEN || "CaneZabCI8dkNeGWEProWa3khmDMtYMkibmGriPWtS8HJn3ph";

    // Build request URL with query and optional token param
    const url = `${baseUrl}?query=${encodeURIComponent(userInput)}${authToken ? `&auth_token=${encodeURIComponent(authToken)}` : ""}`;

    // Compose payload with routes as required by backend
    const payload = {
      routes: routes.map(r => ({
        name: r.name,
        path: r.path,
        description: r.description
      }))
    };

    const response = await axios.post(url, payload, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      }
    });

    const statusCode = response?.data?.status_code;
    const routeFromApi = response?.data?.data?.route || null;
    const contextFromApi = response?.data?.data?.context || "";

    if (statusCode === 200 && routeFromApi && allowedPaths.has(routeFromApi)) {
      return {
        suggestedPath: routeFromApi,
        confidence: 0.95,
        explanation: contextFromApi || "Route selected by UI-Agent"
      };
    }

    // If API returns a path not in allowed list or missing, fallback
    return fallbackSearch(userInput);

  } catch (error) {
    console.error("Error calling UI-Agent backend:", error);
    return fallbackSearch(userInput);
  }
}

// Fallback search using navigation_paths.json when Azure OpenAI is not available or invalid
function fallbackSearch(userInput) {
  const input = userInput.toLowerCase();
  const routes = Array.isArray(navData?.routes) ? navData.routes : [];
  
  // Common phrase mappings for fallback
  const commonPhrases = {
    'dashboard': '/Dashboard',
    'home': '/home',
    'profile': '/profile',
    'employee': '/Employees',
    'employees': '/Employees',
    'leave': '/SubmitLeave',
    'timesheet': '/SubmitTimesheet',
    'claims': '/SubmitClaims',
    'asset': '/AssetInventory',
    'assets': '/AssetInventory',
    'holiday': '/holidays',
    'holidays': '/holidays',
    'announcement': '/announcements',
    'announcements': '/announcements'
  };
  
  // Check for common phrases first
  for (const [phrase, path] of Object.entries(commonPhrases)) {
    if (input.includes(phrase)) {
      const route = routes.find(r => r.path === path);
      if (route) {
        return {
          suggestedPath: path,
          confidence: 0.9,
          explanation: `Found common phrase match: ${route.name}`
        };
      }
    }
  }
  
  // Try to find exact matches across path, name, and description
  const exactMatches = routes.filter(route => 
    route.path.toLowerCase().includes(input) ||
    (route.name || '').toLowerCase().includes(input) ||
    (route.description || '').toLowerCase().includes(input)
  );
  
  if (exactMatches.length > 0) {
    const bestMatch = exactMatches[0];
    return {
      suggestedPath: bestMatch.path,
      confidence: 0.8,
      explanation: `Found exact match: ${bestMatch.name}`
    };
  }
  
  // Try partial matches
  const partialMatches = routes.filter(route => {
    const routeWords = ((route.name || '') + ' ' + (route.description || '')).toLowerCase().split(/\s+/);
    const inputWords = input.split(' ');
    
    return inputWords.some(inputWord => 
      routeWords.some(routeWord => routeWord.includes(inputWord) || inputWord.includes(routeWord))
    );
  });
  
  if (partialMatches.length > 0) {
    const bestMatch = partialMatches[0];
    return {
      suggestedPath: bestMatch.path,
      confidence: 0.6,
      explanation: `Found partial match: ${bestMatch.name}`
    };
  }
  
  // No matches found
  return {
    suggestedPath: null,
    confidence: 0,
    explanation: "No navigation path found for this request"
  };
}
